import React, { useState, useMemo } from "react";

import { Link, useNavigate } from "react-router";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { StarRating } from "./ui/star-rating";
import { Input } from "./ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Plus, ArrowUp, ArrowDown, Search } from "lucide-react";
import { Badge } from "./ui/badge";
import { glassIcon, kilnLogo } from "../helpers/logoHelpers";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "./ui/table";

import useFirebaseKilns from "../hooks/useFirebaseKilns";
import useFirebaseProjects from "../hooks/useFirebaseProjects";
import useFirebaseFirings from "../hooks/useFirebaseFirings";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import usePreferenceActions from "../hooks/usePreferenceActions";
import ProjectCard from "./ProjectCard";
import PageHeader from "./PageHeader";
import { convertLengthUnit } from "../helpers/unitHelpers";
import { findSuitablePhoto } from "../helpers/photoHelpers";

const ProjectListPage = () => {
  const [detail, setDetail] = useState<boolean | null>(null);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const navigate = useNavigate();
  const preferences = useFirebasePreferences();
  const { editPreferences } = usePreferenceActions();

  // Initialize detail from preferences once loaded
  if (detail === null && preferences) {
    setDetail(preferences.project_list_type === "list");
  }

  const desired_unit = (preferences && preferences.length_unit) || "mm";

  const projects = useFirebaseProjects();
  const kilns = useFirebaseKilns();
  const all_firings = useFirebaseFirings();

  const toggleDetail = (value: boolean) => {
    setDetail(value);
    editPreferences({ ...preferences, project_list_type: value ? "list" : "grid" });
  };

  const renderDetailButton = () => {
    return detail ? (
      <div className="inline-flex rounded-md shadow-sm">
        <Button variant="outline" onClick={() => toggleDetail(false)}>Cards</Button>
        <Button>Detail</Button>
      </div>
    ) : (
      <div className="inline-flex rounded-md shadow-sm">
        <Button>Cards</Button>
        <Button variant="outline" onClick={() => toggleDetail(true)}>Detail</Button>
      </div>
    );
  };

  const detailedList = detail && preferences?.detailed_card;

  const firingCountsByProject = useMemo(() => {
    if (!all_firings) return {};
    const counts: Record<string, number> = {};
    for (const f of Object.values(all_firings)) {
      if (f.project_id) {
        counts[f.project_id] = (counts[f.project_id] || 0) + 1;
      }
    }
    return counts;
  }, [all_firings]);

  const unsorted_project_array = projects && Object.values(projects);

  const allTags = useMemo(() => {
    if (!unsorted_project_array) return [];
    const tagSet = new Set<string>();
    unsorted_project_array.forEach(p => p.tags?.forEach(t => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [unsorted_project_array]);

  const project_array = useMemo(() => {
    if (!unsorted_project_array) return null;
    let arr = [...unsorted_project_array];

    if (selectedTag) {
      arr = arr.filter(p => p.tags?.includes(selectedTag));
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const firingsArray = all_firings ? Object.values(all_firings) : [];
      arr = arr.filter(p => {
        if (p.name?.toLowerCase().includes(term)) return true;
        if (p.tags?.join(" ").toLowerCase().includes(term)) return true;
        if (p.glass?.toLowerCase().includes(term)) return true;
        if (p.materials) {
          for (const mat of Object.values(p.materials)) {
            if (mat.description?.toLowerCase().includes(term)) return true;
            if (mat.name?.toLowerCase().includes(term)) return true;
            if (mat.glass_reference?.toLowerCase().includes(term)) return true;
          }
        }
        for (const f of firingsArray) {
          if (f.project_id === p.id) {
            if (f.name?.toLowerCase().includes(term)) return true;
            if (f.notes?.toLowerCase().includes(term)) return true;
          }
        }
        return false;
      });
    }

    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortField) {
        case "name":
          return dir * (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
        case "size":
          return dir * (((a.width ?? 0) * (a.depth ?? 0) * (a.thickness ?? 0)) - ((b.width ?? 0) * (b.depth ?? 0) * (b.thickness ?? 0)));
        case "kiln":
          return dir * (kilns?.[a.kiln!]?.name || "").toLowerCase().localeCompare((kilns?.[b.kiln!]?.name || "").toLowerCase());
        case "glass":
          return dir * (a.glass || "").toLowerCase().localeCompare((b.glass || "").toLowerCase());
        case "rating":
          return dir * ((a.stars || 0) - (b.stars || 0));
        default:
          return 0;
      }
    });
    return arr;
  }, [unsorted_project_array, sortField, sortDir, kilns, searchTerm, selectedTag, all_firings]);

  const handleSort = (field: any) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: any }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="inline-block h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline-block h-3 w-3 ml-1" />
    );
  };

  if (projects === undefined) {
    return (
      <div className="space-y-6">
        <PageHeader title="Projects" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-[4/3] bg-muted animate-pulse" />
              <CardHeader><div className="h-5 w-3/4 bg-muted animate-pulse rounded" /></CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        actions={
          <>
            {renderDetailButton()}
            <Link to="/new_project">
              <Button><Plus className="h-4 w-4 mr-1" />New Project</Button>
            </Link>
          </>
        }
      />
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={selectedTag} onValueChange={(val) => setSelectedTag(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {allTags.map(tag => (
              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {detail ? (
        project_array && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Photo</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                  Name <SortIcon field="name" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("size")}>
                  Size <SortIcon field="size" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("kiln")}>
                  Kiln <SortIcon field="kiln" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("glass")}>
                  Glass Type <SortIcon field="glass" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("rating")}>
                  Rating <SortIcon field="rating" />
                </TableHead>
                {detailedList && <TableHead>Tags</TableHead>}
                {detailedList && <TableHead>Notes</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {project_array.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/projects/${row.id}`)}
                >
                  <TableCell>
                    <img className="h-9 w-9" src={findSuitablePhoto(row, "small").uri} alt="" />
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {convertLengthUnit(row.length_unit, desired_unit, row.width ?? 0)} {"\u00d7"} {convertLengthUnit(row.length_unit, desired_unit, row.depth ?? 0)} {"\u00d7"} {convertLengthUnit(row.length_unit, desired_unit, row.thickness ?? 0)} {desired_unit}
                  </TableCell>
                  <TableCell>
                    {detailedList ? (
                      <span className="inline-flex items-center gap-1">
                        {row.kiln && kilns?.[row.kiln] && (
                          <img src={kilnLogo(kilns[row.kiln].manufacturer)} alt="" className="h-6 w-6 object-contain" />
                        )}
                        <Badge variant="secondary">{firingCountsByProject[row.id!] || 0}</Badge>
                      </span>
                    ) : (
                      kilns?.[row.kiln!]?.name
                    )}
                  </TableCell>
                  <TableCell>
                    {detailedList ? (
                      <span className="inline-flex items-center gap-1">
                        {glassIcon(row.glass)}
                        <Badge variant="secondary">{Object.keys(row.materials || {}).length}</Badge>
                      </span>
                    ) : (
                      row.glass
                    )}
                  </TableCell>
                  <TableCell>
                    <StarRating maxRating={5} rating={row.stars} />
                  </TableCell>
                  {detailedList && (
                    <TableCell>{row.tags?.join(", ")}</TableCell>
                  )}
                  {detailedList && (
                    <TableCell className="max-w-[200px] truncate">{row.notes}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {project_array &&
            project_array.map(
              (item) =>
                item && (
                  <ProjectCard {...item} key={item.id} kilnName={kilns && kilns[item.kiln!]?.name} />
                )
            )}

          <Link to={`/new_project`}>
            <Card className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow h-full flex flex-col">
              <div className="aspect-[4/3] bg-muted flex items-center justify-center">
                <Plus className="h-12 w-12 text-muted-foreground/40" />
              </div>
              <CardHeader>
                <CardTitle className="text-base">Add New Project</CardTitle>
              </CardHeader>
            </Card>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProjectListPage;
